/**
 * `Page` — the app's page layout primitive: a centered, max-width, padded column
 * (see the `size` prop). It supports two scroll models; choose per page:
 *
 * 1. Document scroll (DEFAULT — use for most pages). Render your chrome
 *    (`Page.TitleContainer` / `Page.Title`) and content directly; the page grows
 *    with its content and the app shell scrolls the whole document (the title
 *    scrolls away with it).
 *
 *      <Page>
 *        <Page.TitleContainer><Page.Title>Title</Page.Title></Page.TitleContainer>
 *        … content …
 *      </Page>
 *
 * 2. Fill / no document scrollbar (OPT-IN). Render a `Page.Fill` and the page
 *    becomes a bounded full-height column: `Page.Header` pins at the top while
 *    `Page.Fill` takes the remaining height and scrolls *itself*. Use this for
 *    split / workspace layouts (e.g. a list scrolling beside a detail pane).
 *    Just rendering `Page.Fill` enables it — you never write `vh`/`calc`/`zoom`.
 *
 *      <Page>
 *        <Page.Header>… title, tabs …</Page.Header>   // pinned, natural height
 *        <Page.Fill>… split / scrollable body …</Page.Fill>  // fills the rest
 *      </Page>
 *
 * Action bar: render `<ActionBar show>` / `<SaveBar show>` anywhere inside the
 * `Page` (they self-register). The `Page` reserves bottom padding while a bar is
 * shown so the fixed bar never occludes content — in both scroll models.
 *
 * Parts: `Page.Title`, `Page.Subtitle`, `Page.TitleContainer`, `Page.Header`,
 * `Page.Fill`, `Page.BackButton`.
 */
import { BackButton } from "@/components/composite";
import { ActionBarContext, useActionBarPadding } from "@/components/composite/action-bar";
import { cn } from "@/lib/utils/cn";
import { cva, type VariantProps } from "class-variance-authority";
import React from "react";

/**
 * A `Page.Fill` descendant calls this to make its enclosing `Page` a bounded,
 * full-height column (so the Fill region can cap its children and scroll them
 * internally instead of the document).
 */
const SetPageFillContext = React.createContext<(fill: boolean) => void>(() => {});

const rootVariants = cva("mx-auto flex w-full flex-col px-2 py-8 sm:px-4", {
  variants: {
    size: {
      default: "max-w-7xl",
      prose: "max-w-prose",
      small: "max-w-3xl",
      medium: "max-w-5xl",
      large: "max-w-7xl",
      xl: "max-w-[120rem]",
      full: "max-w-full",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

export type RootProps =
  & React.ComponentPropsWithRef<"div">
  & VariantProps<typeof rootVariants>
  & { fill?: boolean; };

const Root: React.FC<RootProps> = ({ children, className, size, fill, ...props }) => {
  const { visible, setVisible } = useActionBarPadding();
  // Flipped on by a `Page.Fill` descendant. A filled page becomes a bounded
  // `h-full` column so the Fill region scrolls internally; otherwise the page
  // stays document-flow and scrolls as a whole.
  const [autoFill, setAutoFill] = React.useState(false);
  const isFill = fill || autoFill;
  return (
    <ActionBarContext value={setVisible}>
      <SetPageFillContext value={setAutoFill}>
        <div
          className={cn(rootVariants({ size }), isFill && "h-full", visible && "pb-20", className)}
          {...props}
        >
          {children}
        </div>
      </SetPageFillContext>
    </ActionBarContext>
  );
};

const Title: React.FC<React.ComponentPropsWithRef<"h1">> = ({ children, className, ...props }) => {
  return (
    <h1 className={cn("text-2xl font-bold", className)} {...props}>
      {children}
    </h1>
  );
};

const Subtitle: React.FC<React.ComponentPropsWithRef<"p">> = ({
  children,
  className,
  ...props
}) => {
  return (
    <p className={cn("text-muted-foreground", className)} {...props}>
      {children}
    </p>
  );
};

const TitleContainer: React.FC<React.ComponentPropsWithRef<"div">> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div className={cn("flex items-center gap-3", className)} {...props}>
      {children}
    </div>
  );
};

/**
 * Page chrome (title, tabs, etc.). When the page also renders a `Page.Fill`,
 * this header stays pinned (`shrink-0`) while `Page.Fill` takes the remaining
 * height; on a normal document page it simply scrolls with the content.
 */
const Header: React.FC<React.ComponentPropsWithRef<"div">> = ({ className, ...props }) => {
  return <div className={cn("shrink-0", className)} {...props} />;
};

/**
 * Opt-in container that fills the space remaining below the header and scrolls
 * *itself* rather than the document — for pages that should not have a
 * document-level scrollbar (split / workspace layouts). Rendering this makes the
 * enclosing `Page` a bounded full-height column, so height flows from flexbox and
 * consumers never reason about `vh`/`calc`; the fixed ActionBar buffer is handled
 * by the `Page`.
 *
 * Pages that omit `Page.Fill` stay document-flow and scroll as a whole. Pass
 * `className="overflow-hidden"` if a child manages all scrolling itself.
 */
const Fill: React.FC<React.ComponentPropsWithRef<"div">> = ({ className, ...props }) => {
  const setPageFill = React.useContext(SetPageFillContext);
  // Make the enclosing `Page` a bounded `h-full` column before paint, so this
  // region caps its children (they scroll internally, not the document).
  React.useLayoutEffect(() => {
    setPageFill(true);
    return () => setPageFill(false);
  }, [setPageFill]);
  return (
    <div className={cn("flex min-h-0 flex-1 flex-col overflow-y-auto", className)} {...props} />
  );
};

export const Page = Object.assign(Root, {
  Title,
  Subtitle,
  TitleContainer,
  Header,
  Fill,
  BackButton,
});
